import { FC, useEffect, useRef, useState } from 'react'
import ContainerDnDInvoice from './ContainerDnDInvoice'
import { InvoiceMultiPageSetting, InvoicePages } from '../../interfaces/Invoices'
import Carousel from 'react-bootstrap/Carousel'
import jsPDF from 'jspdf'
import { toJpeg, toPng } from 'html-to-image'
import { useIntl } from 'react-intl'
import { Dropdown } from 'rsuite'
import Swal from 'sweetalert2'
import { sendFileInvoiceGenerated } from '../../api/InvoicesCRUD'
import useAccessToken from '../../hooks/AccessToken'
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Spinner } from 'react-bootstrap'
import { InvoicePreviewWhatsAppCaption } from './InvoiceComponentParts'
import { mutate } from 'swr'
import { Pipeline } from '../../interfaces'
import { RecentChatHistoryPipeline } from '../../interfaces/Chat/RecentChatHistory'
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useParams } from 'react-router-dom'

const API_URL = process.env.REACT_APP_API_URL

const WEB_URL = process.env.REACT_APP_ENV === 'development' ? 'https://crm-dev.wooblazz.com/' : process.env.REACT_APP_ENV === 'production' ? 'https://crm.wooblazz.com/' : 'http://localhost:3011/'

interface InvoicePreviewProps {
  routerContext: 'invoice' | 'pipeline' | 'web-view-preview'
  column: any[]
  multiPages: InvoicePages[]
  extractPages: boolean
  setMultiPages: React.Dispatch<React.SetStateAction<InvoicePages[] | null>>
  lead?: Pipeline | RecentChatHistoryPipeline
  invoice?: InvoiceMultiPageSetting
  format?: 'pdf' | 'image' | 'all'
  showCaraousel?: boolean
  showDownloadBtn?: boolean
  showFormButton?: boolean
  handleInvoiceModalClose?: (value: boolean) => void
  closeModalRef?: any
  tokenWebView?: string
}

export const InvoicePreview: FC<InvoicePreviewProps> = ({
  routerContext,
  column,
  multiPages,
  extractPages,
  setMultiPages,
  lead,
  invoice,
  format,
  showCaraousel = true,
  showDownloadBtn = false,
  showFormButton = false,
  handleInvoiceModalClose,
  closeModalRef,
  tokenWebView
}) => {
  const intl = useIntl()
  const token = useAccessToken()
  const [pageIndex, setPageIndex] = useState(0)
  const [autoSlide, setAutoSlide] = useState<number | null>(3000000)
  const [btnLoading, setbtnLoading] = useState<boolean>(false)
  const [showCaptionWAModal, setShowCaptionWAModal] = useState<boolean>(false)
  const { token: tokenEncryptWebView } = useParams()

  const pageRefs = useRef<HTMLDivElement[]>([])
  const handleSelect = (selectedIndex: any) => {
    setPageIndex(selectedIndex)
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setAutoSlide(null) // Hentikan slide saat tab tidak aktif
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const sendInvoiceToWhastApp = async (fileBase64: string) => {
    if (fileBase64 && invoice && handleInvoiceModalClose) {
      setbtnLoading(true)
      try {
        // Hapus prefix "data:application/pdf;base64," jika ada
        const base64Data = fileBase64.split(',')[1]

        // Konversi base64 ke binary
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const fileBlob = new Blob([byteArray], { type: 'application/pdf' })

        // Buat File dari Blob
        const file = new File([fileBlob], `${invoice.name ?? 'invoice-send-whatsapp'}.pdf`, { type: 'application/pdf' })

        const req = await sendFileInvoiceGenerated(routerContext === 'web-view-preview' ? tokenWebView! : token, invoice.id, file)
        const res = req.data

        const masterLogInvoiceUrl = `${API_URL}invoices/generated/list?leadId=${lead?.id}`
        mutate(masterLogInvoiceUrl, undefined, { revalidate: true })
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: intl.formatMessage({ id: "ALERT.TITLE.SUCCESS" }),
            text: intl.formatMessage({ id: 'INVOICE.ALERT.SUCCESS_SEND_WHATSAPP' }, { title: '' }) + ``,
            confirmButtonText: 'Ok',
          }).then(async (result) => {
            mutate(masterLogInvoiceUrl, undefined, { revalidate: true })
            if (result.isConfirmed) {
              handleInvoiceModalClose(true);
            }
          });
        }
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response.data.message
        })
      } finally {
        setbtnLoading(false)
      }
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: intl.formatMessage({ id: 'INVOICE.ERROR_BAD_REQUEST' }),
      })
    }
  }

  return (
    <>
      {showCaraousel && (
        <div className='d-flex flex-column position-relative w-100 py-8' style={{ background: '#3e3e3e' }}>
          {btnLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(255, 255, 255, 0.3)',
                zIndex: 1000,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Spinner animation="border" />
            </div>
          )}
          <div
            style={{
              width: window.innerWidth < 768 ? 'auto' : `720px`,
              border: '1px solid #ccc',
            }}
            className='d-flex bg-white justify-content-between m-auto p-4 rounded gap-4'
          >
            <div className='d-flex flex-column gap-2'>
              <small className='fw-bold'>
                {intl.formatMessage({ id: 'INVOICE.PANEL.PREVIEW_PAGES' })}
              </small>
              <b>{multiPages[pageIndex].name}</b>
            </div>
            <div
              className={`d-flex flex-column gap-2 ${showDownloadBtn && routerContext === 'pipeline' ? 'text-center' : 'text-end'
                }`}
            >
              <small className='fw-bold'>
                {intl.formatMessage({ id: 'INVOICE.LABEL.DND_ITEMS' })}
              </small>
              <b>
                {multiPages[pageIndex].fields.length > 0
                  ? multiPages[pageIndex].fields.length
                  : intl.formatMessage({ id: 'INVOICE.LABEL.DND_ITEMS_NONE' })}
              </b>
            </div>
            {routerContext === 'pipeline' && showDownloadBtn && (
              <div className='d-flex text-end gap-2'>
                <Dropdown
                  renderToggle={(props: any, ref: any) => {
                    return (
                      <button
                        {...props}
                        ref={ref}
                        type='button'
                        disabled={btnLoading}
                        className='d-flex btn btn-primary btn-sm gap-2 items-center'
                      >
                        {intl.formatMessage(
                          { id: 'INVOICE.BUTTON.DOWNLOAD' },
                          { title: intl.formatMessage({ id: 'INVOICE.LABEL.FILES' }) }
                        )}
                      </button>
                    )
                  }}
                  title={`${intl.formatMessage({ id: 'FORM.ACTION.ADD' })}`}
                  noCaret
                  placement='bottomStart'
                >
                  <>
                    <Dropdown.Item
                      onClick={() => {
                        generateInvoicePDF('download', invoice, intl, multiPages, pageRefs, pageIndex, 'original')
                      }}
                    >
                      {intl.formatMessage({ id: 'INVOICE.LABEL.DOWNLOAD_PDF_ORIGINAL_SIZE' })}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        generateInvoicePDF('download', invoice, intl, multiPages, pageRefs, pageIndex, 'a4')
                      }}
                    >
                      Download PDF A4
                    </Dropdown.Item>
                  </>
                  <Dropdown.Item
                    onClick={() => generateInvoiceImage('download', invoice, intl, pageRefs, 'all')}
                  >
                    Download {intl.formatMessage({ id: 'INVOICE.LABEL.IMAGE_ALL' })}
                  </Dropdown.Item>
                </Dropdown>
              </div>
            )}
            {/* {
              routerContext === 'web-view-preview' && showDownloadBtn && tokenEncryptWebView && (
                <button className='d-flex btn btn-primary btn-sm items-center my-auto' onClick={() => {
                  window.open(`${WEB_URL}web-view-invoice/${tokenEncryptWebView}/${invoice?.progress?.id}/${invoice?.leadId}/autodownload`, '_blank')
                }}>Download</button>
              )
            } */}
          </div>
          <Carousel
            activeIndex={pageIndex}
            onSelect={handleSelect}
            interval={autoSlide}
            pause={false}
            prevIcon={
              multiPages.length > 1 ? (
                <ArrowBackIos
                  sx={{
                    fontSize: 40,
                    color: "white",
                    stroke: "black",
                    strokeWidth: 1.5,
                  }}
                />
              ) : null
            }
            nextIcon={
              multiPages.length > 1 ? (
                <ArrowForwardIos
                  sx={{
                    fontSize: 40,
                    color: "white",
                    stroke: "black",
                    strokeWidth: 1.5,
                  }}
                />
              ) : null
            }
            style={{ minHeight: '1100px', background: '#3e3e3e' }}
          >
            {Array.from({ length: multiPages.length }).map((_, index) => (
              <Carousel.Item>
                <ContainerDnDInvoice
                  routerContext={routerContext}
                  stepType='preview'
                  column={column}
                  extractPages={extractPages}
                  pageItems={multiPages[index]}
                  setMultiPages={setMultiPages}
                />
              </Carousel.Item>
            ))}
          </Carousel>
          <div
            style={{
              border: '1px solid #ccc',
            }}
            className='d-flex bg-white justify-content-between m-auto p-4 rounded gap-4 mt-6'
          >
            {routerContext === 'pipeline' && showFormButton && handleInvoiceModalClose && (
              <div className='d-flex flex-column gap-2'>
                <button
                  type='button'
                  className='d-flex btn btn-secondary gap-2 items-center'
                  onClick={() => handleInvoiceModalClose(false)}
                  disabled={btnLoading}
                >
                  {intl.formatMessage({ id: 'INVOICE.LABEL.CANCEL_INVOICE' })}
                </button>
              </div>
            )}
            <div className='d-flex flex-column gap-2'>
              <div>
                <button
                  type='button'
                  disabled={btnLoading}
                  className='d-flex btn btn-success gap-2 items-center'
                  onClick={() => setShowCaptionWAModal(true)}
                >
                  {intl.formatMessage({ id: 'FORM.LABEL.CAPTION' })} WhatsApp
                </button>
              </div>
            </div>
            {((routerContext === 'pipeline' && showFormButton && handleInvoiceModalClose) || (routerContext === 'web-view-preview')) && (

              <div className='d-flex flex-column gap-2'>
                <Dropdown
                  renderToggle={(props: any, ref: any) => {
                    return (
                      <button
                        {...props}
                        disabled={btnLoading}
                        ref={ref}
                        type='button'
                        className='d-flex btn btn-success gap-2 items-center'
                      >
                        {intl.formatMessage({ id: 'INVOICE.LABEL.SEND_WHATSAPP' })} <WhatsAppIcon />
                      </button>
                    )
                  }}
                  title={`${intl.formatMessage({ id: 'FORM.ACTION.ADD' })}`}
                  noCaret
                  placement='topStart'
                >
                  <Dropdown.Item
                    onClick={() => {
                      generateInvoicePDF(
                        'form-base64',
                        invoice,
                        intl,
                        multiPages,
                        pageRefs,
                        pageIndex,
                        'original'
                      ).then((base64) => {
                        if (base64) {
                          sendInvoiceToWhastApp(base64)
                        }
                      })
                    }}
                  >
                    {intl.formatMessage({ id: 'INVOICE.LABEL.SEND_PDF_ORIGINAL_SIZE' })}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      generateInvoicePDF(
                        'form-base64',
                        invoice,
                        intl,
                        multiPages,
                        pageRefs,
                        pageIndex,
                        'a4'
                      ).then((base64) => {
                        if (base64) {
                          sendInvoiceToWhastApp(base64)
                        }
                      })
                    }}
                  >
                    {intl.formatMessage({ id: 'INVOICE.LABEL.SEND_PDF_A4' })}
                  </Dropdown.Item>
                </Dropdown>
              </div>
            )}
          </div>
        </div>
      )}
      {invoice && (
        <InvoicePreviewWhatsAppCaption showCaptionWAModal={showCaptionWAModal} handleCaptionWAModalClose={(value: boolean) => setShowCaptionWAModal(value)} invoice={invoice} />
      )}
      <RenderDOMPagesInvoice
        lead={lead}
        routerContext={routerContext}
        pageRefs={pageRefs}
        column={column}
        multiPages={multiPages}
        extractPages={extractPages}
        setMultiPages={setMultiPages}
      />
    </>
  )
}

export const generateInvoicePDF = async (
  context: 'form-base64' | 'download',
  invoice: InvoiceMultiPageSetting | undefined,
  intl: ReturnType<typeof useIntl>,
  multiPages: InvoicePages[],
  pageRefs: React.MutableRefObject<HTMLDivElement[]>,
  pageIndex: number | 'all' = 'all',
  paperSize: 'a4' | 'original'
): Promise<void | string> => {
  let filename = invoice?.name?.slice(0, 30) || `invoice_download_${new Date().getTime()}`

  await document.fonts.ready

  if (!multiPages || multiPages.length === 0) return

  try {
    const pageElFirst = pageRefs.current[0]
    if (!pageElFirst) return

    const firstImgData = await toJpeg(pageElFirst, { skipFonts: true })

    const firstImg = new Image()
    firstImg.src = firstImgData
    await new Promise((resolve) => (firstImg.onload = resolve))

    let firstImgWidth = multiPages[0]?.imageFile?.width ?? firstImg.width
    let firstImgHeight = multiPages[0]?.imageFile?.height ?? firstImg.height

    if (paperSize === 'a4') {
      const a4Width = 2480 // Portrait
      const a4Height = 3508 // Portrait
      const scale = Math.min(a4Width / firstImgWidth, a4Height / firstImgHeight)
      firstImgWidth *= scale
      firstImgHeight *= scale
    }

    // Tentukan orientasi otomatis
    const firstOrientation = firstImgWidth > firstImgHeight + 100 ? 'l' : 'p'
    const pageWidth = firstOrientation === 'l' ? 3508 : 2480
    const pageHeight = firstOrientation === 'l' ? 2480 : 3508

    // Inisialisasi PDF
    const pdf = new jsPDF({
      orientation: firstOrientation,
      unit: 'px',
      format: paperSize === 'a4' ? [pageWidth, pageHeight] : [firstImgWidth, firstImgHeight],
    })

    if (paperSize === 'a4') {
      // 300 dpi
      const scale = Math.min(pageWidth / firstImgWidth, pageHeight / firstImgHeight)
      firstImgWidth *= scale
      firstImgHeight *= scale
      // Tambahkan halaman pertama dengan posisi tengah
      const xOffset = (pageWidth - firstImgWidth) / 2
      const yOffset = (pageHeight - firstImgHeight) / 2
      pdf.addImage(firstImgData, 'JPEG', xOffset, yOffset, firstImgWidth, firstImgHeight)
    } else if (paperSize === 'original') {
      pdf.addImage(firstImgData, 'JPEG', 0, 0, firstImgWidth, firstImgHeight)
    }

    // Loop untuk halaman berikutnya
    for (let i = 1; i < multiPages.length; i++) {
      const pageEl = pageRefs.current[i]
      if (!pageEl) continue

      const imgData = await toJpeg(pageEl, { skipFonts: true })
      const img = new Image()
      img.src = imgData
      await new Promise((resolve) => (img.onload = resolve))

      let imgWidth = multiPages[i]?.imageFile?.width ?? img.width
      let imgHeight = multiPages[i]?.imageFile?.height ?? img.height

      // Tentukan orientasi halaman otomatis
      const orientation = imgWidth > imgHeight + 100 ? 'l' : 'p'
      const pageWidth = orientation === 'l' ? 3508 : 2480
      const pageHeight = orientation === 'l' ? 2480 : 3508

      if (paperSize === 'a4') {
        const scale = Math.min(pageWidth / imgWidth, pageHeight / imgHeight)
        imgWidth *= scale
        imgHeight *= scale
        pdf.addPage([pageWidth, pageHeight], orientation)
        const xOffset = (pageWidth - imgWidth) / 2
        const yOffset = (pageHeight - imgHeight) / 2
        pdf.addImage(imgData, 'JPEG', xOffset, yOffset, imgWidth, imgHeight)
      } else if (paperSize === 'original') {
        pdf.addPage([imgWidth, imgHeight], orientation)
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight)
      }
    }

    if (context === 'download') {
      pdf.save(`${filename}-${paperSize}.pdf`)
    } else {
      const base64 = pdf.output('datauristring')
      return base64
    }
  } catch (error: any) {
    Swal.fire({
      title: intl.formatMessage({ id: 'INVOICE.ALERT.ERROR' }),
      text: intl.formatMessage({ id: 'INVOICE.ALERT.FAILED_GENERATE_INVOICE' }, { title: '.pdf' }),
      icon: 'error',
    })
  }
}



export const generateInvoiceImage = async (
  context: 'form-base64' | 'download',
  invoice: InvoiceMultiPageSetting | undefined,
  intl: ReturnType<typeof useIntl>,
  pageRefs: React.MutableRefObject<HTMLDivElement[]>,
  pageIndex: number | 'all' = 'all'
): Promise<void | string> => {
  let filename = invoice?.name?.slice(0, 30) || `invoice_screenshot_${new Date().getTime()}`

  await document.fonts.ready

  if (!pageRefs.current || pageRefs.current.length === 0) return

  try {
    if (pageIndex === 'all') {
      for (let i = 0; i < pageRefs.current.length; i++) {
        const pageRef = pageRefs.current[i] as HTMLDivElement | null
        if (!pageRef) continue

        const dataUrl = await toPng(pageRef, { skipFonts: true })
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${filename}-${i}.png`
        link.click()
      }
    } else if (typeof pageIndex === 'number') {
      const pageRef = pageRefs.current?.[pageIndex] as HTMLDivElement | null
      if (!pageRef) return

      const dataUrl = await toPng(pageRef, { skipFonts: true })

      if (context === 'download') {
        const link = document.createElement('a')
        link.href = dataUrl
        link.download = `${filename}-${pageIndex}.png`
        link.click()
      }
    }
  } catch (error) {
    Swal.fire({
      title: intl.formatMessage({ id: 'INVOICE.ALERT.ERROR' }),
      text: intl.formatMessage(
        { id: 'INVOICE.ALERT.FAILED_GENERATE_INVOICE' },
        { title: 'Screenshoot' }
      ),
      icon: 'error',
    })
  }
}

export const RenderDOMPagesInvoice: FC<
  InvoicePreviewProps & { pageRefs: React.MutableRefObject<HTMLDivElement[]> }
> = ({ routerContext, pageRefs, column, multiPages, extractPages, setMultiPages }) => {
  return (
    <div className='d-flex flex-column w-100 visually-hidden' style={{ position: 'absolute', left: '-9999px', top: 0 }}>
      {Array.from({ length: multiPages.length }).map((_, index) => (
        <ContainerDnDInvoice
          key={index}
          routerContext={routerContext}
          stepType='preview'
          column={column}
          extractPages={extractPages}
          pageItems={multiPages[index]}
          setMultiPages={setMultiPages}
          pageRef={(el) => {
            pageRefs.current[index] = el as HTMLDivElement
          }}
        />
      ))}
    </div>
  )
}
